// ============================================
// FILE: frontend/src/components/messaging/ConnectionsList.jsx
// PURPOSE: Display user's connections with unread counts
// FIXED: Text overflow + collapsible design
// ============================================

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { MessageCircle, Clock, Loader2, RefreshCw, ChevronDown, X } from 'lucide-react';
import { format } from 'date-fns';

// ========================================
// MAIN COMPONENT
// ========================================

export function ConnectionsList({ 
  connections = [], 
  loading = false,
  onRefresh,
  autoRefresh = true,
  refreshInterval = 10000
}) {
  console.log('[ConnectionsList] Rendering with connections:', {
    count: connections.length,
    loading,
    autoRefresh
  });

  // ========================================
  // STATE
  // ========================================

  const [selectedConnection, setSelectedConnection] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true); // NEW: Collapsible state

  // ========================================
  // REFS FOR POLLING
  // ========================================

  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // ========================================
  // AUTO-REFRESH POLLING
  // ========================================

  useEffect(() => {
    if (!autoRefresh || !onRefresh) {
      console.log('[ConnectionsList] Auto-refresh disabled or no callback');
      return;
    }

    if (isPollingRef.current) {
      console.log('[ConnectionsList] Polling already active');
      return;
    }

    console.log('[ConnectionsList] 🔄 Starting auto-refresh polling...', {
      interval: refreshInterval
    });

    isPollingRef.current = true;

    pollingIntervalRef.current = setInterval(() => {
      console.log('[ConnectionsList] ⏱️ Polling tick - refreshing connections');
      if (onRefresh) {
        onRefresh();
      }
    }, refreshInterval);

    return () => {
      console.log('[ConnectionsList] Cleanup: stopping polling');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [autoRefresh, onRefresh, refreshInterval]);

  // ========================================
  // EVENT HANDLERS
  // ========================================

  const handleSelectConnection = (connection) => {
    console.log('[ConnectionsList] Selected connection:', connection.id);
    setSelectedConnection(connection.id);
  };

  const handleManualRefresh = async () => {
    console.log('[ConnectionsList] Manual refresh clicked');
    if (onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
        console.log('[ConnectionsList] ✅ Manual refresh completed');
      } catch (err) {
        console.error('[ConnectionsList] ❌ Manual refresh failed:', err);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'both_contacted':
        return 'bg-blue-100 text-blue-800';
      case 'deal_in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'deal_completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'deal_failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // ========================================
  // LOADING STATE
  // ========================================

  if (loading && connections.length === 0) {
    console.log('[ConnectionsList] Initial loading state');
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32 p-3 sm:p-4 md:p-6">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-muted-foreground" />
            <p className="text-xs sm:text-sm text-muted-foreground">Loading connections...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ========================================
  // EMPTY STATE
  // ========================================

  if (!connections || connections.length === 0) {
    console.log('[ConnectionsList] Empty state');
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center h-32 gap-2 sm:gap-3 p-3 sm:p-4 md:p-6">
          <MessageCircle className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
          <p className="text-xs sm:text-sm text-muted-foreground">No connections yet</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground text-center">
            Connections will appear here once you're matched
          </p>
        </CardContent>
      </Card>
    );
  }

  // ========================================
  // RENDER
  // ========================================

  console.log('[ConnectionsList] Rendering connections list');

  return (
    <Card>
      {/* Collapsible Header */}
      <CardHeader 
        className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm sm:text-base md:text-lg">Your Connections</CardTitle>
            {/* Auto-refresh indicator */}
            {autoRefresh && isPollingRef.current && (
              <div className="hidden sm:flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground">
                <RefreshCw className="w-2 h-2 sm:w-3 sm:h-3 animate-spin" />
                <span>Auto-updating</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleManualRefresh();
              }}
              disabled={loading || isRefreshing}
              className="text-xs h-8"
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                  <span className="hidden sm:inline">Refreshing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline ml-2">Refresh</span>
                </>
              )}
            </Button>
            
            {isExpanded ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {/* Collapsible Content */}
      {isExpanded && (
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          {/* Subtle loading indicator while refreshing */}
          {loading && connections.length > 0 && (
            <div className="flex items-center justify-center py-2 mb-2">
              <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Updating connections...</span>
              </div>
            </div>
          )}

          <div className="space-y-2 sm:space-y-3">
            {connections.map(connection => (
              <Link
                key={connection.id}
                to={`/connections/${connection.id}/chat`}
                onClick={() => handleSelectConnection(connection)}
              >
                <div
                  className={`p-2 sm:p-3 md:p-4 rounded-lg border-2 transition-all cursor-pointer hover:border-primary ${
                    selectedConnection === connection.id
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:bg-muted/50'
                  }`}
                >
                  {/* Connection Header */}
                  <div className="flex items-start justify-between gap-2 sm:gap-4 mb-2 sm:mb-3">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      {/* Avatar */}
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                        <AvatarImage 
                          src={connection.otherParty?.photoURL}
                          alt={connection.otherParty?.fullName}
                        />
                        <AvatarFallback>
                          {connection.otherParty?.fullName?.[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      {/* Info - FIXED: Added min-w-0 and truncate */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-xs sm:text-sm truncate">
                          {connection.otherParty?.fullName}
                        </p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                          {connection.otherParty?.email}
                        </p>
                      </div>
                    </div>

                    {/* Unread badge */}
                    {connection.unreadMessageCount > 0 && (
                      <Badge variant="default" className="bg-red-600 h-5 sm:h-6 px-1.5 sm:px-2 rounded-full text-[10px] sm:text-xs flex-shrink-0">
                        {connection.unreadMessageCount}
                      </Badge>
                    )}
                  </div>

                  {/* Connection Footer */}
                  <div className="flex items-center justify-between gap-2">
                    <Badge className={`${getStatusBadgeColor(connection.status)} text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-1`}>
                      {formatStatus(connection.status)}
                    </Badge>

                    <div className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground">
                      <Clock className="h-2 w-2 sm:h-3 sm:w-3 flex-shrink-0" />
                      <span>
                        {format(new Date(connection.createdAt), 'MMM d')}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default ConnectionsList;